import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FormTemplate,
  FormTemplateQuestion,
  FormTemplateQuestionFieldsKindEnum,
  FormTemplateVersion,
  Task,
  TaskStatusEnum,
} from 'graphql-common';
import { ApolloError } from '@apollo/client/errors';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Typography from '@lib/components/Typography/Typography';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import QuestionFieldsPreview from 'components/FormTemplateBuilder/QuestionFieldsPreview/QuestionFieldsPreview';
import FormTemplateBuilderQuestionView from 'components/FormTemplateBuilder/FormTemplateBuilderQuestionView/FormTemplateBuilderQuestionView';
import TaskMenuBar from 'components/TaskMenuBar/TaskMenuBar';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import QuestionTypeChip from 'components/FormTemplateBuilder/QuestionTypeChip/QuestionTypeChip';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import VersionIcon from '@lib/assets/icons/version_icon.svg?react';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import FormTemplateLinkedDocumentsSection from 'components/FormTemplate/FormTemplateLinkedDocumentsSection';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import styles from './TaskFormView.module.scss';

interface Props {
  idParam: string;
  onOpenEditTaskDetails: () => void;
  refetchTaskFormTemplate?: () => void;
  taskData: Task;
  taskFormTemplate: FormTemplate;
  taskFormTemplateGraphQLErrors?: ApolloError;
  taskFormTemplateLoading: boolean;
  taskFormTemplateVersion: FormTemplateVersion;
}

type ActionsProps = Pick<Props, 'taskData' | 'onOpenEditTaskDetails'> & {
  canUpdateFormTemplate: boolean;
};

function TaskFormViewActions(props: ActionsProps) {
  const { canUpdateFormTemplate, onOpenEditTaskDetails, taskData } = props;
  const { t } = useTranslation();
  if (
    taskData.status === TaskStatusEnum.Archived ||
    taskData.status === TaskStatusEnum.Completed
  ) {
    return null;
  }
  return canUpdateFormTemplate ? (
    <Button
      buttonText={t('edit-form')}
      buttonSize={ButtonSizes.large}
      buttonType={ButtonTypes.primaryOutlined}
      leftIcon="edit"
      onClick={onOpenEditTaskDetails}
    />
  ) : (
    <Tooltip body={t('tooltip-disable-form-edit')} maxWidth={190}>
      <Button
        buttonText={t('edit-form')}
        buttonSize={ButtonSizes.large}
        buttonType={ButtonTypes.primaryOutlined}
        leftIcon="lock"
        disabled
      />
    </Tooltip>
  );
}

export default function TaskFormView(props: Props) {
  const { t } = useTranslation();
  const {
    idParam,
    onOpenEditTaskDetails,
    refetchTaskFormTemplate,
    taskData,
    taskFormTemplate,
    taskFormTemplateGraphQLErrors,
    taskFormTemplateLoading,
    taskFormTemplateVersion,
  } = props;
  const { getUserAccessByCompany, isFullAdmin }: ContextProps =
    useOutletContext();
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const formTemplateId = taskFormTemplate?.id;
  const hasLinkedDocumentations = taskFormTemplate?.hasLinkedDocumentations;
  const name = taskFormTemplate?.name;
  const description = taskFormTemplate?.description;
  const categoryName = taskFormTemplate?.category?.name;
  const categoryColor = colorToChipType(taskFormTemplate?.category?.color);
  const canUpdateFormTemplate = taskData?.formTemplateVersion?.canUpdate?.value;
  const formTemplateVersion = taskFormTemplateVersion?.version;
  // EDM-locked + non-Full-Admin: drop questions whose every field is a
  // Document — the user has no way to upgrade the plan, so there's nothing
  // useful to render.
  const questions = useMemo(() => {
    const list = (taskFormTemplateVersion?.questions ||
      []) as FormTemplateQuestion[];
    if (!(isViewLocked && !isFullAdmin)) return list;
    return list.filter((question) =>
      question.fields?.some(
        ({ kind }) => kind !== FormTemplateQuestionFieldsKindEnum.Document,
      ),
    );
  }, [taskFormTemplateVersion?.questions, isViewLocked, isFullAdmin]);

  if (taskFormTemplateGraphQLErrors) {
    return (
      <ErrorPage
        status={getStatusFromError(taskFormTemplateGraphQLErrors)}
        description={getDescriptionFromError(taskFormTemplateGraphQLErrors)}
      />
    );
  }

  return (
    <PageBody withoutHeader>
      <TaskMenuBar id={idParam} />
      <DashboardContentMaxWidth>
        {taskFormTemplateLoading ? (
          <CircleLoader />
        ) : (
          <div>
            <WhiteBox className={styles.mainBlock}>
              <DashboardTitleHeader
                title={name}
                actions={
                  <TaskFormViewActions
                    canUpdateFormTemplate={canUpdateFormTemplate}
                    onOpenEditTaskDetails={onOpenEditTaskDetails}
                    taskData={taskData}
                  />
                }
                bottomSpace="none"
              />
              <div>
                <Chip
                  type={categoryColor}
                  label={categoryName}
                  leftIcon="circle"
                />
              </div>
              {hasTextEditorValue(description) && (
                <div>
                  <TextEditor
                    readOnly
                    value={description}
                    className={styles.description}
                  />
                </div>
              )}
              <hr className={entityViewStyles.hrLine} />
              <div
                className={classNames(
                  entityViewStyles.infoItem,
                  entityViewStyles.infoItemCentered,
                )}
              >
                <p className={entityViewStyles.infoItemLabel}>
                  <VersionIcon />
                  <Typography variant="caption">{t('version')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContentBox}>
                  <Chip
                    label={
                      formTemplateVersion ? `v${formTemplateVersion}` : '-'
                    }
                    type={ChipTypes.chipGray}
                    size={ChipSizes.large}
                  />
                </div>
              </div>
              {formTemplateId && (
                <>
                  <hr className={entityViewStyles.hrLine} />
                  <FormTemplateLinkedDocumentsSection
                    formTemplateId={formTemplateId}
                    hasLinkedDocumentations={!!hasLinkedDocumentations}
                    onUnlinked={refetchTaskFormTemplate}
                  />
                </>
              )}
            </WhiteBox>
            {questions?.map((question) => {
              const kind = _get(question, [
                FormTemplateFieldNames.Fields,
                0,
                FormTemplateFieldNames.Kind,
              ]);
              return (
                <WhiteBox
                  className={styles.questionBlock}
                  key={question.position}
                >
                  <QuestionTypeChip
                    kind={kind}
                    className={
                      kind ===
                      FormTemplateQuestionFieldsKindEnum.ImportantNotice
                        ? styles.questionTypeChip
                        : undefined
                    }
                  />
                  <FormTemplateBuilderQuestionView value={question}>
                    <QuestionFieldsPreview
                      fields={question.fields}
                      value={question}
                    />
                  </FormTemplateBuilderQuestionView>
                </WhiteBox>
              );
            })}
          </div>
        )}
      </DashboardContentMaxWidth>
    </PageBody>
  );
}
